import React, {
    useCallback
} from 'react';

import {
    atom,
    atomFamily,
    useRecoilValue,
    useRecoilState,
    useSetRecoilState,
    useRecoilCallback,
    RecoilRoot
} from 'recoil';

// Atoms
//
// configurationSchema - singleton (within the provider), holds the curation configuration schema
// viewStateValues - atom family to maps propName to view state value.
// focusedInstanceId - atom containing the id of the currently focused instance.
// instanceConfigurationValues - atom family to map instanceId to the bag of values

// This CURATION_INSTANCE_ID used when there is a null instance id provided (e.g., at the curation level).
export const CURATION_INSTANCE_ID = '&CURATION_INSTANCE_ID&';

function updateUrl(propsToSet, shouldMerge) {

    if (!shouldMerge) {
        const keysToNull = _.difference(
            _.union(
                _.keys(RivalIQ.apps.Dashboard.router.router.loadedCuration.attributes),
                _.keys(RivalIQ.apps.Dashboard.router.router.loadedCuration.queryProps)
            ),
            _.keys(propsToSet)
        );

        _.each(keysToNull, key => {
            propsToSet[key] ??= null;
        });
    }

    const updatedPath = RivalIQ.apps.Dashboard.router.router.loadedCuration.getPath(propsToSet);

    const newUrl = new URL(`http://origin${updatedPath}`);
    const isNewLocationDifferent = newUrl.search !== window.location.search ||
        newUrl.pathname !== window.location.pathname;

    const currentFocusedInstanceId = RivalIQ.apps.Dashboard.router.router.loadedCuration.getProp('instanceId');

    if (isNewLocationDifferent || currentFocusedInstanceId !== propsToSet.instanceId) {
        RivalIQ.execute('history:navigate', updatedPath, {
            trigger: currentFocusedInstanceId !== propsToSet.instanceId
        });
    }
}

const configurationSchemaAtom = atom({
    key: 'configurationSchema',
    default: {}
});

function getViewStateModel(propKey) {
    switch (propKey) {

        case 'timezone':
            return RivalIQ.state.landscape;

        case 'email':
            return RivalIQ.state.user;

        case 'plan':
            return RivalIQ.state.account;

        default:
            return RivalIQ.state.view;
    }
}

const viewStateSetterOverrides = {
    timePeriod: (proxiedSetter, timePeriod) => {
        // NOTE the `dates` field must exist for persisted time periods, we
        // cannot use `serializeDates` here because it won't use the value for
        // `timePeriod` that is passed into the function, it retrieves one from
        // `state.timePeriods` instead which could/will be wrong! (🙈)
        const serializedTimePeriod = _.extend({
            dates: {}
        }, timePeriod);

        const attrs = {
            timePeriod: serializedTimePeriod
        };

        if (timePeriod.id === RivalIQ.enums.timePeriod.type.CUSTOM) {
            attrs.customTimePeriod = serializedTimePeriod;
        }

        RivalIQ.state.view.set(attrs);

        proxiedSetter(serializedTimePeriod);
    }
};

const viewStateEffect = function (propKey) {
    return ({setSelf, onSet}) => {

        const viewStateModel = getViewStateModel(propKey);

        setSelf(_.cloneDeep(viewStateModel.get(propKey)));

        // listen for changes to the view state and update this value.
        const viewStateChangedListener = () => {
            setSelf(_.cloneDeep(viewStateModel.get(propKey)));
        };
        viewStateModel.on(`change:${propKey}`, viewStateChangedListener);

        if (!_.has(viewStateSetterOverrides, propKey)) {
            // As we don't have a use-case for writing these from widgets, we're going to error out instead of allowing
            // the newValue to be persisted to state.
            onSet((newValue) => {
                RivalIQ.logger.error(`attempted to write view state prop, should be read-only. ${propKey} -> ${newValue}`);
            });
        }

        return () => {
            viewStateModel.off(`change:${propKey}`, viewStateChangedListener);
        };
    };
};

const viewStateValues = atomFamily({
    key: 'viewStateValues',
    default: propKey => _.cloneDeep(getViewStateModel(propKey).get(propKey)),
    effects: propKey => [
        viewStateEffect(propKey)
    ]
});

const focusedInstanceIdAtom = atom({
    key: 'focusedInstanceId',
    default: null,
    effects: [
        focusedInstanceIdEffect
    ]
});

const instanceConfigurationValues = atomFamily({
    key: 'instanceConfigurationValues',
    default: null,
    effects: instanceId => [
        instanceConfigurationValueEffect(instanceId)
    ]
});

function instanceConfigurationValueEffect(instanceId) {
    return ({onSet, getPromise}) => {
        onSet(async newValue => {
            const configurationSchema = await getPromise(configurationSchemaAtom);

            const curationPropsConfig = configurationSchema.curationPropsConfig;

            const curationProps = await getPromise(instanceConfigurationValues(CURATION_INSTANCE_ID));

            if (configurationSchema.disableUrlPersistence && configurationSchema.disableUrlPersistence(curationProps)) {
                return;
            }

            // determine if there are any curation-level props that need to be set in the url
            let propsToSet = _.reduce(curationPropsConfig, (memo, propConfig, propKey) => {
                if (propConfig.urlPersistence) {
                    memo[propKey] = curationProps[propKey];
                }
                return memo;
            }, {});

            // If there's a widget that is currently focused, make sure that it's props are in the url as needed.
            const focusedInstanceId = await getPromise(focusedInstanceIdAtom);
            if (instanceId !== CURATION_INSTANCE_ID) {
                if (focusedInstanceId === instanceId) {
                    propsToSet = _.reduce(configurationSchema.widgetPropsConfig[instanceId], (memo, propConfig, propKey) => {
                        if (propConfig.urlPersistence) {
                            memo[propKey] = newValue[propKey];
                        }
                        return memo;
                    }, propsToSet);
                }
            }

            if (focusedInstanceId) {
                propsToSet.instanceId = focusedInstanceId;
            }

            updateUrl(propsToSet, true);
        });
    };
}


function focusedInstanceIdEffect({onSet, getPromise}) {
    onSet(async (newInstanceId, oldInstanceId) => {

        const configurationSchema = await getPromise(configurationSchemaAtom);
        const curationProps = await getPromise(instanceConfigurationValues(CURATION_INSTANCE_ID));

        const updatedProps = _.reduce(configurationSchema.curationPropsConfig, (memo, propConfig, propKey) => {
            if (propConfig.urlPersistence) {
                memo[propKey] = curationProps[propKey];
            }
            return memo;
        }, {});

        // if we're going from a focused widget back to the curation, we need to capture any props that were set on the widget
        // and push them into wProps.
        if (oldInstanceId && oldInstanceId !== CURATION_INSTANCE_ID) {
            const widgetPropsConfig = configurationSchema.widgetPropsConfig[oldInstanceId];
            if (widgetPropsConfig) {
                const widgetProps = await getPromise(instanceConfigurationValues(oldInstanceId));

                updatedProps.wProps = {
                    [oldInstanceId]: {}
                };
                _.each(widgetPropsConfig, (propConfig, propKey) => {
                    if (propConfig.urlPersistence && widgetProps[propKey] && widgetProps[propKey] !== configurationSchema.defaultProps?.wProps?.[oldInstanceId]?.[propKey]) {
                        updatedProps.wProps[oldInstanceId][propKey] = widgetProps[propKey];
                    }
                });

                if (_.isEmpty(updatedProps.wProps[oldInstanceId])) {
                    delete updatedProps.wProps;
                }
            }
        }

        // if we're going to a widget details mode, be sure to capture any wProps and push them into the url.
        if (newInstanceId && newInstanceId !== CURATION_INSTANCE_ID) {
            const widgetPropsConfig = configurationSchema.widgetPropsConfig[newInstanceId];
            if (widgetPropsConfig) {
                const widgetProps = await getPromise(instanceConfigurationValues(newInstanceId));
                _.each(widgetPropsConfig, (propConfig, propKey) => {
                    if (propConfig.urlPersistence && widgetProps?.[propKey] && widgetProps[propKey] !== configurationSchema.defaultProps?.wProps?.[newInstanceId]?.[propKey]) {
                        updatedProps[propKey] = widgetProps[propKey];
                    }
                });
            }
        }

        if (!_.isEmpty(RivalIQ.apps.Dashboard.router.router.loadedCuration.queryProps?.wProps)) {
            updatedProps.wProps = _.extend({}, RivalIQ.apps.Dashboard.router.router.loadedCuration.queryProps?.wProps, updatedProps.wProps);
        }

        if (newInstanceId) {
            updatedProps.instanceId = newInstanceId;
        } else {
            updatedProps.instanceId = undefined;
        }

        updateUrl(updatedProps, false);
    });
}

// This list is was taken from dashboard/models/curation.js
const VIEW_STATE_PROPS = [
    'focusCompanyId',
    'landscapeId',
    'companySetDescriptor',
    'timePeriod',
    'timezone',
    'activeSources',
    'redactedSources',
    'supported_sources',
    'supported_main_time_periods',
    'plan',
    'email',
    'account_id'
];
function _isViewStateConfiguration(configurationKey) {
    if (_.includes(VIEW_STATE_PROPS, configurationKey)) {
        return true;
    } else {
        return false;
    }
}

function _findAtom(instanceId, propKey) {
    instanceId ||= CURATION_INSTANCE_ID;

    const isViewStateConfig = _isViewStateConfiguration(propKey);

    const atom = (isViewStateConfig ?
        viewStateValues(propKey) :
        instanceConfigurationValues(instanceId));

    return atom;
}

export function useGetAccountId() {
    return RivalIQ.state.account.get('id');
}

export function useGetLandscapeId() {
    return RivalIQ.state.view.get('landscapeId');
}

// returns just the current value of "propKey", scoped to instanceId. This hook and useCurationConfigState allows a widget to override
// a curation prop for it's own view; e.g., if the curation has prop "foo" set to "bar", an instance can override "foo" to "notbar".
// In this case, only that instance will see "notbar" as the value of "foo" - the curation and other instances will still see "foo".
export function useCurationConfigValue(instanceId, propKey) {
    if (arguments.length === 1) {
        propKey = instanceId;
        instanceId = null;
    }

    if (propKey === 'instanceId') {
        throw new Error(`use useGetFocusedInstanceIdValue to interact with the instanceId prop`);
    }

    const curationValue = useRecoilValue(_findAtom(CURATION_INSTANCE_ID, propKey));
    const widgetValue = useRecoilValue(_findAtom(instanceId, propKey));

    const isViewStateConfig = _isViewStateConfiguration(propKey);
    if (isViewStateConfig) {
        return curationValue;
    }

    if (_.has(widgetValue, propKey)) {
        return widgetValue[propKey];
    } else {
        return curationValue[propKey];
    }
}

const computedPropKeyToCurationPropKeys = {
    'dateRanges': ['timePeriod', 'timezone']
};

const curationValueToComputedValue = {
    'dateRanges': ({timePeriod, timezone}) => {
        const dateRanges = _.reduce(['main', 'compare'], (memo, range) => {
            // NOTE we know that the existent `timePeriod` from state is in
            // pseudo-UTC (see #37058), so we need to first convert this faux
            // date into the timezone by changing only the offset, before then
            // TRULY converting it into UTC via `toISOString`
            const periodStartTz = moment.utc(timePeriod[`${range}PeriodStart`]).tz(timezone, true);
            const periodEndTz = moment.utc(timePeriod[`${range}PeriodEnd`]).tz(timezone, true);
            return _.merge(memo, {
                [range]: {
                    start: periodStartTz.toISOString(),
                    end: periodEndTz.toISOString()
                }
            });
        }, {});
        return dateRanges;
    }
};

export function getComputedValue(propKey, state) {
    const computedValueHandler = curationValueToComputedValue[propKey];
    if (!computedValueHandler) {
        throw new Error(`unrecognized computed value prop key: ${propKey}`);
    }

    const result = computedValueHandler(state);

    return result;
}

// NOTE this hook is designed to accomodate computed (derived) values from
// curation configuration state. these values are read only, and will change in
// value as their backing data changes in value (like a view). while there are
// many reasons we might want something like this, the impetus was to provide a
// mechanism to bypass the legacy, broken `timePeriod`, which has numerous
// shortcomings and thus pitfalls (see #36547, #37058, #37074).
export function useComputedCurationConfigValue(instanceId, propKey) {
    if (arguments.length === 1) {
        propKey = instanceId;
        instanceId = null;
    }

    const state = _.pick({
        // NOTE for the time being, we have to call these even if we don't need
        // them, given the rules of hooks (they should not be called in a loop the
        // order must be deterministic and preserved)
        timePeriod: useCurationConfigValue(instanceId, 'timePeriod'),
        timezone: useCurationConfigValue(instanceId, 'timezone')
    }, computedPropKeyToCurationPropKeys[propKey]);

    const result = getComputedValue(propKey, state);

    return result;
}

function useProxiedSetter(propKey, widgetSetter) {
    // This proxy is necessary as we're storing a bag of props by instanceId, but expose it as a single prop value.
    const proxiedSetter = useCallback((newValue) => {
        widgetSetter(currentValue => {
            const valueToSet = _.isFunction(newValue) ? newValue(currentValue[propKey]) : newValue;
            const updatedProps = _.defaults({
                [propKey]: valueToSet
            }, currentValue);

            if (_.isUndefined(valueToSet)) {
                delete updatedProps[propKey];
            } else if (_.isNull(valueToSet)) {
                updatedProps[propKey] = null;
            }

            return updatedProps;
        });
    }, [propKey, widgetSetter]);

    return proxiedSetter;
}

// returns the value + a setter
export function useCurationConfigState(instanceId, propKey) {
    if (arguments.length === 1) {
        propKey = instanceId;
        instanceId = null;
    }

    if (propKey === 'instanceId') {
        throw new Error(`use useGetFocusedInstanceIdState to interact with the instanceId prop`);
    }

    const curationValue = useRecoilValue(_findAtom(CURATION_INSTANCE_ID, propKey));
    const [widgetValue, widgetSetter] = useRecoilState(_findAtom(instanceId, propKey));

    const proxiedSetter = useProxiedSetter(propKey, widgetSetter);

    const isViewStateConfig = _isViewStateConfiguration(propKey);
    if (isViewStateConfig) {
        const viewStateErrorSetter = (newValue) => {
            RivalIQ.logger.error(`attempted to write view state prop, should be read-only. ${propKey} -> ${newValue}`);
            throw new Error(`View state props are read-only. Attempted to write to prop ${propKey}`);
        };
        return [widgetValue, viewStateErrorSetter];
    }

    // intentionally returning `widgetSetter` in both cases so that widgets can override curation state for just themselves.
    if (_.has(widgetValue, propKey)) {
        return [widgetValue[propKey], proxiedSetter];
    } else {
        return [curationValue[propKey], proxiedSetter];
    }
}

// returns a setter; this does not subscribe the caller to changes in the value
export function useSetCurationConfigState(instanceId, propKey) {
    if (arguments.length === 1) {
        propKey = instanceId;
        instanceId = null;
    }

    if (propKey === 'instanceId') {
        throw new Error(`use useGetFocusedInstanceIdState to interact with the instanceId prop`);
    }

    const widgetSetter = useSetRecoilState(_findAtom(instanceId, propKey));

    const proxiedSetter = useProxiedSetter(propKey, widgetSetter);

    const isViewStateConfig = _isViewStateConfiguration(propKey);
    if (isViewStateConfig) {
        const viewStateSetter = _.get(viewStateSetterOverrides, propKey, (proxiedSetter, newValue) => {
            RivalIQ.logger.error(`attempted to write view state prop, should be read-only. ${propKey} -> ${newValue}`);
            throw new Error(`View state props are read-only. Attempted to write to prop ${propKey}`);
        });
        return _.partial(viewStateSetter, proxiedSetter);
    }

    return proxiedSetter;
}

// hook to update multiple props for an instance
export function useUpdateProps(instanceId, {force = false} = {}) {
    const setter = useSetRecoilState(instanceConfigurationValues(instanceId || CURATION_INSTANCE_ID));

    const proxiedSetter = useCallback((newProps) => {
        setter(currentProps => {
            return (!force ? _.defaults(newProps, currentProps) : newProps);
        });
    }, [setter]); // eslint-disable-line react-hooks/exhaustive-deps

    return proxiedSetter;
}


export function useGetFocusedInstanceIdValue() {
    return useRecoilValue(focusedInstanceIdAtom);
}

export function useGetFocusedInstanceIdState() {
    return useRecoilState(focusedInstanceIdAtom);
}

// return a function. That function can be called with a function that will get called with the current state values.
export function useSerializeConfigurations(callbackFcn, deps) {

    return useRecoilCallback(function ({snapshot}) {
        return async () => {
            const allProps = _.defaults({
                wProps: {}
            }, await snapshot.getPromise(instanceConfigurationValues(CURATION_INSTANCE_ID)));

            const nodes = snapshot.getNodes_UNSTABLE({
                isInitialized: true
            });

            for (const node of nodes) {
                const matches = node.key.match(/instanceConfigurationValues__"(.+)"$/);
                if (matches) {
                    const instanceId = matches[1];
                    if (instanceId !== CURATION_INSTANCE_ID) {
                        allProps.wProps[instanceId] = await snapshot.getPromise(instanceConfigurationValues(instanceId)) || {};
                    }
                }
            }

            callbackFcn(allProps);
        };
    }, deps);
}

function initializePropsFromUrl(propsConfig) {
    const initialProps = {};
    if (_.some(propsConfig, 'urlPersistence')) {

        const searchStr = window.location.search.substring(1);
        const params = Marionette.Dashboard.utils.params.queryStringToParams(searchStr);
        _.each(propsConfig, (config, propName) => {
            if (config.urlPersistence) {
                const override = params[propName];
                if (override) {
                    initialProps[propName] = override;
                }
            }
        });
    }

    return initialProps;
}

function validateAndNormalizeCurationSchema(configurationSchema) {
    configurationSchema = _.cloneDeep(configurationSchema) || {};

    configurationSchema.curationId ||= RivalIQ.apps.Dashboard.router.router?.loadedCuration?.id;

    configurationSchema.curationPropsConfig ||= {};
    configurationSchema.widgetPropsConfig ||= {};

    // Having instanceId persisted to the url is necessary for our router framework to handle summary+details mode.
    configurationSchema.curationPropsConfig.instanceId = {
        urlPersistence: true
    };

    // reading format from the query string is necessary for export to function.
    configurationSchema.curationPropsConfig.format = {
        urlPersistence: true
    };

    // onlyInstanceId is a special prop to export a single widget in summary mode.
    configurationSchema.curationPropsConfig.onlyInstanceId = {
        urlPersistence: true
    };

    configurationSchema.curationPropsConfig.wProps = {
        urlPersistence: true
    };

    const curationStatePropOverlap = _.intersection(VIEW_STATE_PROPS, _.keys(configurationSchema.curationPropsConfig));
    if (curationStatePropOverlap.length) {
        throw new Error(`Cannot define a curation prop with the same key as a view state prop: ${curationStatePropOverlap.join(', ')}`);
    }

    _.each(configurationSchema.widgetPropsConfig, (propsConfig, instanceId) => {
        const widgetStatePropOverlap = _.intersection(VIEW_STATE_PROPS, _.keys(propsConfig));
        if (widgetStatePropOverlap.length) {
            throw new Error(`Cannot define a widget prop with the same key as a view state prop; ${instanceId} - ${widgetStatePropOverlap.join(', ')}`);
        }

        if (widgetStatePropOverlap.instanceId) {
            throw new Error(`instanceId is a special curation level prop that cannot be used by a widget ${instanceId}`);
        }
    });

    return configurationSchema;
}

export function ConfigurationProvider({defaultProps, configurationSchema, children}) {

    const focusedInstanceId = defaultProps?.instanceId || null;
    const initializer = ({set}) => {
        configurationSchema = validateAndNormalizeCurationSchema(configurationSchema);
        configurationSchema.defaultProps = _.clone(defaultProps);

        set(configurationSchemaAtom, configurationSchema);

        set(focusedInstanceIdAtom, focusedInstanceId);

        // Initialize the curation props. These will be default props, overridden by any props in the url.
        const initialCurationProps = _.omit(defaultProps, 'wProps', 'instanceId');
        _.extend(initialCurationProps, initializePropsFromUrl(configurationSchema.curationPropsConfig));

        set(instanceConfigurationValues(CURATION_INSTANCE_ID), initialCurationProps);

        // Initialize the individual widget props. These will be default props, overridden by any props in the url (from the wProps query param).
        const wProps = _.merge({}, defaultProps.wProps, initialCurationProps.wProps);

        const singleInstanceId = focusedInstanceId || initialCurationProps.onlyInstanceId;
        if (singleInstanceId) {
            const instancePropsFromUrl = initializePropsFromUrl(configurationSchema.widgetPropsConfig[singleInstanceId]);
            wProps[singleInstanceId] = _.merge({}, wProps[singleInstanceId], instancePropsFromUrl);
        }

        _.each(wProps, (props, instanceId) => {
            set(instanceConfigurationValues(instanceId), props);
        });
    };

    return (
        <RecoilRoot initializeState={initializer}>
            {children}
        </RecoilRoot>
    );
}
